import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import TooltipTypography from 'src/components/TooltipTypography/TooltipTypography';
import { useTranslation } from 'react-i18next';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import { useSelector } from 'react-redux';
import { selectBusinessQueryOptions } from 'src/store/selectors/businessSelectors';
import {
  MetricsViewOptions,
  QuoteTrimesterField
} from 'src/utils/constants/business';

const buildQuarterTooltip = (quarter, t) => {
  if (!quarter) return '';
  return [
    `${t('BUSINESS_VIEW.METRICS.IN_REVIEW')}: ${
      quarter.quotesInReview.count
    } (${formatPositiveAmount(quarter.quotesInReview.amount)})`,
    `${t('BUSINESS_VIEW.METRICS.IN_DRAFT')}: ${
      quarter.quotesInDraft.count
    } (${formatPositiveAmount(quarter.quotesInDraft.amount)})`,
    `${t('BUSINESS_VIEW.METRICS.ACCEPTED')}: ${
      quarter.quotesAccepted.count
    } (${formatPositiveAmount(quarter.quotesAccepted.amount)})`,
    `${t('BUSINESS_VIEW.METRICS.REJECTED')}: ${
      quarter.quotesRejected.count
    } (${formatPositiveAmount(quarter.quotesRejected.amount)})`,
    `${t('BUSINESS_VIEW.METRICS.ARCHIVED')}: ${
      quarter.quotesArchived.count
    } (${formatPositiveAmount(quarter.quotesArchived.amount)})`
  ].join(' | ');
};

const QuarterCell = ({ quarter, t, quoteStatus }) => {
  if (!quarter) return <TableCell align="center">—</TableCell>;
  const field = quarter[quoteStatus] ?? quarter[QuoteTrimesterField.ACCEPTED];
  return (
    <TableCell align="center">
      <TooltipTypography
        title={buildQuarterTooltip(quarter, t)}
        text={formatPositiveAmount(field?.amount)}
      />
    </TableCell>
  );
};

QuarterCell.propTypes = {
  quarter: PropTypes.shape({
    quotesInReview: PropTypes.shape({
      count: PropTypes.number,
      amount: PropTypes.number
    }),
    quotesInDraft: PropTypes.shape({
      count: PropTypes.number,
      amount: PropTypes.number
    }),
    quotesAccepted: PropTypes.shape({
      count: PropTypes.number,
      amount: PropTypes.number
    }),
    quotesRejected: PropTypes.shape({
      count: PropTypes.number,
      amount: PropTypes.number
    }),
    quotesArchived: PropTypes.shape({
      count: PropTypes.number,
      amount: PropTypes.number
    })
  }),
  t: PropTypes.func.isRequired,
  quoteStatus: PropTypes.string
};

const MetricsByTrimesterRow = ({ id, name, byPeriod }) => {
  const { t } = useTranslation();
  const { metricsViewOption, quoteStatus } = useSelector(
    selectBusinessQueryOptions
  );

  const nameLinkRedirect =
    metricsViewOption === MetricsViewOptions.DISTRIBUTOR
      ? `/distributors/business/${id}`
      : `/distributor-profile/${id}`;

  const getQuarter = quarterNumber =>
    byPeriod?.find(p => p.periodLabel === quarterNumber);

  return (
    <TableRow hover>
      <TableCell>{id}</TableCell>
      <TableCell>
        <Link to={nameLinkRedirect}>{name}</Link>
      </TableCell>
      <QuarterCell quarter={getQuarter(1)} t={t} quoteStatus={quoteStatus} />
      <QuarterCell quarter={getQuarter(2)} t={t} quoteStatus={quoteStatus} />
      <QuarterCell quarter={getQuarter(3)} t={t} quoteStatus={quoteStatus} />
      <QuarterCell quarter={getQuarter(4)} t={t} quoteStatus={quoteStatus} />
    </TableRow>
  );
};

MetricsByTrimesterRow.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  byPeriod: PropTypes.array
};

MetricsByTrimesterRow.defaultProps = {
  byPeriod: []
};

export default MetricsByTrimesterRow;
